import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "lucide-react";

export type Crumb = { label?: string; to?: string; node?: ReactNode };

// Compact page header for the admin work portal: a breadcrumb trail instead of a big icon + title.
// The last crumb is the current page (not a link). An optional subtitle + right-aligned action slot
// keep pages consistent. Mirrors the Origin UI breadcrumb styling (muted, sm, chevron separators).
export function AdminPageHeader({
  items,
  subtitle,
  action,
}: {
  items: Crumb[];
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <>
      {/* Sticky top bar, same height (h-14) as the sidebar's "Mijn KUVO Woerden" brand header, with a
          bottom border to separate it. It breaks out of the main's padding (-mx/-mt) so it spans the
          full content width and sits flush at the top, then re-applies the inner padding. */}
      <div className="sticky top-0 z-20 -mx-5 -mt-6 mb-5 flex h-14 items-center justify-between gap-3 border-b border-border bg-background px-5 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
        <nav aria-label="breadcrumb" className="min-w-0">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm sm:gap-2">
            {items.map((c, i) => {
              const last = i === items.length - 1;
              return (
                <li key={`${c.label ?? "crumb"}-${i}`} className="inline-flex items-center gap-1.5 sm:gap-2">
                  {i > 0 && <ChevronRightIcon className="size-3.5 shrink-0 text-muted-foreground/70" aria-hidden />}
                  {c.node ? (
                    c.node
                  ) : last || !c.to ? (
                    <span aria-current={last ? "page" : undefined} className={last ? "truncate font-medium text-foreground" : "text-muted-foreground"}>
                      {c.label}
                    </span>
                  ) : (
                    <Link to={c.to} className="text-muted-foreground transition-colors hover:text-foreground">
                      {c.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {subtitle && <p className="-mt-1 mb-5 text-sm text-muted-foreground">{subtitle}</p>}
    </>
  );
}
